import { GlobalProductNames, GlobalSubProductNames } from "@constants/products";
import {
  MAAnalysisTooltipDataDTO,
  MAAnalysisTooltipDTO,
} from "@models/managed-accounts/entities/step/fee";
import {
  InvestmentSelectionDTO,
  PortfolioInvestmentTypeSelection,
  PortfolioTemplateFeeCalculatorDataDTO,
  SelectedInvestmentDTO,
} from "@models/managed-accounts/entities/step/setup";
import {
  AnalysisFeeCalculatorDTO,
  AnalysisFeePortfolioDTO,
  AnalysisFeePortfolioSetupDTO,
  FeeCalculatorDTO,
  PortfolioAssignmentItemDTO,
  PortfolioAssignmentValidationResultDTO,
  PortfolioFeeCalculatorDataDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { BalanceAvailability } from "@models/platform-analysis/enums/fee/balanceAvailability";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisFeePortfolioSetupType } from "@models/platform-analysis/enums/fee/portfolioSetupType";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { cloneDeep, difference, isEmpty, isNil } from "lodash";
import { FieldPath } from "react-hook-form";
import { AnalysisFeeCalculatorStepConfigs as Configs } from "../../../configs";
import { AnalysisFeeCalculatorErrorMessages as ErrorMessages } from "../../../constants";
import {
  FeeAnalysisBalanceVariableNames,
  FeeAnalysisSpecialProductNames,
  VariableNameToBalanceMap,
} from "./constants";

type PortfolioSetupFieldPath = {
  selectedPortfolioId: FieldPath<AnalysisFeeCalculatorDTO>;
  isMissingInvestment: FieldPath<AnalysisFeeCalculatorDTO>;
  missingInvestmentTooltip: FieldPath<AnalysisFeeCalculatorDTO>;
  isMissingInvestmentOption: FieldPath<AnalysisFeeCalculatorDTO>;
  missingInvestmentOptionTooltip: FieldPath<AnalysisFeeCalculatorDTO>;
  isBelowMinimumInvestmentSize: FieldPath<AnalysisFeeCalculatorDTO>;
  belowMinimumInvestmentSizeTooltip: FieldPath<AnalysisFeeCalculatorDTO>;
  excludedFromFeeAnalysis: FieldPath<AnalysisFeeCalculatorDTO>;
  hasAllocationError: FieldPath<AnalysisFeeCalculatorDTO>;
  hasUnavailableInvestmentOption: FieldPath<AnalysisFeeCalculatorDTO>;
  unavailableInvestmentOptionTooltip: FieldPath<AnalysisFeeCalculatorDTO>;
  hasUnavailableInvestment: FieldPath<AnalysisFeeCalculatorDTO>;
  unavailableInvestmentTooltip: FieldPath<AnalysisFeeCalculatorDTO>;
  hasCashNotAvailableInSuperError: FieldPath<AnalysisFeeCalculatorDTO>;
  hasCashNotAvailableInIDPSError: FieldPath<AnalysisFeeCalculatorDTO>;
};

type GetVariablesParams = {
  productName: string;
  subProductName: string;
  feeCalculators: FeeCalculatorDTO[];
  investmentTypeSelection: PortfolioInvestmentTypeSelection;
  balanceAvailabilities: BalanceAvailabilitiesParams;
};

type GetEditVariablesParams = {
  productName: string;
  subProductName: string;
  updatedFeeCalculators: FeeCalculatorDTO[];
  investmentTypeSelection: PortfolioInvestmentTypeSelection;
  balanceAvailabilities: BalanceAvailabilitiesParams;
};

export type BalanceAvailabilitiesParams = {
  hasIDPSBalance: boolean;
  hasSuperBalance: boolean;
  termDepositsAvailableInIDPS?: boolean; // Term deposits
  termDepositsAvailableInSuper?: boolean;
  unlistedManagedFundsAvailableInIDPS?: boolean; // Managed funds
  unlistedManagedFundsAvailableInSuper?: boolean;
  auListedSecuritiesAvailableInIDPS?: boolean; // Australian listed investments
  auListedSecuritiesAvailableInSuper?: boolean;
  intlListedSecuritiesAvailableInIDPS?: boolean; // International listed investments
  intlListedSecuritiesAvailableInSuper?: boolean;
  fixedIncomeAvailableInIDPS?: boolean; // Unlisted bonds
  fixedIncomeAvailableInSuper?: boolean;
  cashAvailableInIDPS?: boolean; // Cash
  cashAvailableInSuper?: boolean;
};

export type EditPortfolioSetupParams = {
  portfolioId: string;
  setupIndex: number;
};

export type EditPortfolioSetupFeeCalculatorRowPath = {
  weight: FieldPath<AnalysisFeePortfolioDTO>;
  value: FieldPath<AnalysisFeePortfolioDTO>;
};

export type EditPortfolioSetupInvestmentRowPath =
  EditPortfolioSetupFeeCalculatorRowPath & {
    isMissingInvestment: FieldPath<AnalysisFeePortfolioDTO>;
    minimumInvestmentSize: FieldPath<AnalysisFeePortfolioDTO>;
    isFavourited: FieldPath<AnalysisFeePortfolioDTO>;
  };

export const getPortfolioSetupFieldPath = (
  index: number,
): PortfolioSetupFieldPath => {
  const dataPath: FieldPath<AnalysisFeeCalculatorDTO> = `portfolioSetups.${index}`;
  return {
    selectedPortfolioId: `${dataPath}.selectedPortfolioId`,
    isMissingInvestment: `${dataPath}.isMissingInvestment`,
    missingInvestmentTooltip:
      `${dataPath}.missingInvestmentTooltip` as FieldPath<AnalysisFeeCalculatorDTO>,
    isMissingInvestmentOption: `${dataPath}.isMissingInvestmentOption`,
    missingInvestmentOptionTooltip:
      `${dataPath}.missingInvestmentOptionTooltip` as FieldPath<AnalysisFeeCalculatorDTO>,
    isBelowMinimumInvestmentSize: `${dataPath}.isBelowMinimumInvestmentSize`,
    belowMinimumInvestmentSizeTooltip:
      `${dataPath}.belowMinimumInvestmentSizeTooltip` as FieldPath<AnalysisFeeCalculatorDTO>,
    excludedFromFeeAnalysis: `${dataPath}.excludedFromFeeAnalysis`,
    hasAllocationError: `${dataPath}.hasAllocationError`,
    hasUnavailableInvestmentOption: `${dataPath}.hasUnavailableInvestmentOption`,
    unavailableInvestmentOptionTooltip:
      `${dataPath}.unavailableInvestmentOptionTooltip` as FieldPath<AnalysisFeeCalculatorDTO>,
    hasUnavailableInvestment: `${dataPath}.hasUnavailableInvestment`,
    unavailableInvestmentTooltip:
      `${dataPath}.unavailableInvestmentTooltip` as FieldPath<AnalysisFeeCalculatorDTO>,
    hasCashNotAvailableInSuperError: `${dataPath}.hasCashNotAvailableInSuperError`,
    hasCashNotAvailableInIDPSError: `${dataPath}.hasCashNotAvailableInIDPSError`,
  };
};

// Getters for field paths
export const getExcludeFromFeeAnalysisFieldPaths = ({
  setups,
  productId,
}: {
  setups: AnalysisFeePortfolioSetupDTO[];
  productId: string;
}): FieldPath<AnalysisFeeCalculatorDTO>[] => {
  const productItems = setups.filter(
    (item) =>
      item.product?.id === productId &&
      item.setupType === AnalysisFeePortfolioSetupType.SubProduct &&
      !item.hasNoInvestmentData,
  );

  return productItems.map((item) => {
    return getPortfolioSetupFieldPath(setups.indexOf(item))
      .excludedFromFeeAnalysis;
  });
};

export const getInvestmentRowFieldPath = ({
  itemIndex,
}: {
  itemIndex: number;
}): EditPortfolioSetupInvestmentRowPath => {
  const dataPath: FieldPath<AnalysisFeePortfolioDTO> = `selectedInvestments.${itemIndex}`;
  return {
    weight: `${dataPath}.weight`,
    value: `${dataPath}.value`,
    isMissingInvestment: `${dataPath}.isMissingInvestment`,
    minimumInvestmentSize: `${dataPath}.minimumInvestmentSize`,
    isFavourited: `${dataPath}.isFavourited`,
  };
};

export const getFeeCalculatorRowFieldPath = ({
  itemIndex,
}: {
  itemIndex: number;
}): EditPortfolioSetupFeeCalculatorRowPath => {
  const dataPath: FieldPath<AnalysisFeePortfolioDTO> = `feeCalculatorData.${itemIndex}`;
  return {
    weight: `${dataPath}.weight`,
    value: `${dataPath}.value`,
  };
};

// Commons
export const calculateInvestmentValueByWeight = ({
  total,
  weight,
}: {
  total: number;
  weight: number;
}): number => {
  const safeTotal = !isNaN(total) ? total : 0;
  const safeWeight = !isNaN(weight) ? weight : 0;
  const value = (safeTotal * safeWeight) / 100;
  return Number.isInteger(value)
    ? value
    : parseFloat(value.toPrecision(Configs.DefaultPrecision));
};

export const calculateInvestmentWeightByValue = ({
  total,
  value,
}: {
  total: number;
  value: number;
}): number => {
  const safeTotal = !isNaN(total) ? total : 0;
  const safeValue = !isNaN(value) ? value : 0;
  if (safeTotal === 0) return 0;
  const weight = (safeValue / safeTotal) * 100;
  return Number.isInteger(weight)
    ? weight
    : parseFloat(weight.toPrecision(Configs.DefaultPrecision));
};

type SelectedCalculatorVariablesParams = {
  investmentTypeSelection: PortfolioInvestmentTypeSelection;
  balanceAvailabilities: BalanceAvailabilitiesParams;
  isEditMode?: boolean;
};

export const getSelectedCalculatorVariables = ({
  investmentTypeSelection,
  balanceAvailabilities,
  isEditMode,
}: SelectedCalculatorVariablesParams): string[] => {
  const { isChooseTD, isChooseMF, isChooseAU, isChooseIntl, isChooseBond } =
    investmentTypeSelection || {};
  const { hasIDPSBalance, hasSuperBalance } = balanceAvailabilities || {};
  return [
    ...getBaseVariables(hasIDPSBalance, hasSuperBalance, isEditMode),
    ...getTermDepositVariables(isChooseTD),
    ...getManagedFundVariables(isChooseMF),
    ...getAustralianVariables(isChooseAU),
    ...getInternationalVariables(isChooseIntl),
    ...getUnlistBondVariables(isChooseBond),
  ];
};

export const getCreateFeeCalculatorsByVariables = ({
  productName,
  feeCalculators,
  investmentTypeSelection,
  balanceAvailabilities,
}: GetVariablesParams): FeeCalculatorDTO[] => {
  let selectedVariables = getSelectedCalculatorVariables({
    investmentTypeSelection,
    balanceAvailabilities,
  });

  if (isEmpty(selectedVariables)) {
    return [];
  }

  selectedVariables = filterSpecialVariableNames(
    productName,
    selectedVariables,
  );

  const calculatorMap = new Map(
    feeCalculators.map((calc) => [calc.variableName, calc]),
  );

  return selectedVariables.reduce<FeeCalculatorDTO[]>((acc, variable) => {
    const calculatorVariable = variable as AnalysisVariable;
    const calculator = calculatorMap.get(calculatorVariable);

    if (!calculator) {
      return acc;
    }

    const baseCalculator = createDefaultCalculatorDTO(calculator);

    if (
      FeeAnalysisBalanceVariableNames.BalanceAvailabilities.includes(
        calculatorVariable,
      )
    ) {
      const { balanceAvailability, isUnavailable } =
        validateCreatePortfolioBalances({
          variableName: calculatorVariable,
          balanceAvailabilities,
        });

      if (balanceAvailability === BalanceAvailability.Hidden) return acc;
      baseCalculator.balanceAvailability = balanceAvailability;
      baseCalculator.hasUnavailableInvestmentOption = isUnavailable;
    }

    acc.push(baseCalculator);

    return acc;
  }, []);
};

export const getEditFeeCalculatorsByVariables = ({
  productName,
  updatedFeeCalculators,
  investmentTypeSelection,
  balanceAvailabilities,
}: GetEditVariablesParams): FeeCalculatorDTO[] => {
  const { hasIDPSBalance, hasSuperBalance } = balanceAvailabilities || {};
  let selectedVariables = getSelectedCalculatorVariables({
    investmentTypeSelection,
    balanceAvailabilities,
    isEditMode: true,
  });

  if (isEmpty(selectedVariables)) {
    return [];
  }

  selectedVariables = filterSpecialVariableNames(
    productName,
    selectedVariables,
    "edit",
  );

  const calculatorMap = new Map(
    updatedFeeCalculators.map((calc) => [calc.variableName, calc]),
  );

  const specialVariableRecord: Partial<Record<string, boolean>> = {};
  FeeAnalysisSpecialProductNames.VariableMap.forEach((variableList, key) => {
    const isMain = key === productName;
    variableList.forEach((item) => {
      specialVariableRecord[item] = isMain;
    });
  });

  if (!hasIDPSBalance) {
    specialVariableRecord[AnalysisVariable.IDPS_Cash_balance] = false;
  }

  if (!hasSuperBalance) {
    specialVariableRecord[AnalysisVariable.Super_Cash_balance] = false;
  }

  const specialVariableRecordKeys = Object.keys(specialVariableRecord);

  return selectedVariables.reduce<FeeCalculatorDTO[]>((acc, variable) => {
    const calculatorVariable = variable as AnalysisVariable;
    const calculator = calculatorMap.get(calculatorVariable);
    if (isNil(calculator)) {
      return acc;
    }

    const isMain = specialVariableRecord[variable];
    const weight = calculator?.feeCalculatorData?.weight ?? 0;
    const isSpecialVariable = specialVariableRecordKeys.includes(variable);
    const isValidSecondary = !isMain && weight > 0;

    const shouldIncludeCalculator =
      !isSpecialVariable || isValidSecondary || isMain;
    let isMissingInvestment = isSpecialVariable && isValidSecondary;
    const baseCalculator = createEditCalculatorDTO(calculator);

    const invalidIDPSCashBalance =
      calculatorVariable === AnalysisVariable.IDPS_Cash_balance &&
      !hasIDPSBalance;
    const invalidSuperCashBalance =
      calculatorVariable === AnalysisVariable.Super_Cash_balance &&
      !hasSuperBalance;

    if (invalidIDPSCashBalance || invalidSuperCashBalance) {
      if (weight === 0) return acc;
      isMissingInvestment = true;
    }

    if (
      FeeAnalysisBalanceVariableNames.BalanceAvailabilities.includes(
        calculatorVariable,
      )
    ) {
      const { balanceAvailability, isUnavailable } =
        validateEditPortfolioBalances({
          variableName: calculatorVariable,
          balanceAvailabilities,
          hasWeight: Number(weight) > 0,
        });

      if (balanceAvailability === BalanceAvailability.Hidden) return acc;
      baseCalculator.balanceAvailability = balanceAvailability;
      baseCalculator.hasUnavailableInvestmentOption = isUnavailable;
    }

    if (shouldIncludeCalculator) {
      acc.push({
        ...baseCalculator,
        isMissingInvestment: isMissingInvestment,
      });
    }

    return acc;
  }, []);
};

export const mapInvestmentPortfolioForEdit = ({
  selectedInvestments,
  allInvestments,
  total,
  hasSuperBalance,
  hasIDPSBalance,
}: {
  selectedInvestments: SelectedInvestmentDTO[];
  allInvestments: InvestmentSelectionDTO[];
  total: number;
  hasSuperBalance: boolean;
  hasIDPSBalance: boolean;
}): SelectedInvestmentDTO[] => {
  return selectedInvestments.map((selectedInvestment) => {
    const existingInvestment = allInvestments.find(
      (investment) => investment.shCode === selectedInvestment.shCode,
    );

    const weight = selectedInvestment.weight ?? 0;
    const value = calculateInvestmentValueByWeight({
      total,
      weight: weight ?? 0,
    });

    const { availableInSuper, availableInIDPS } = existingInvestment || {};
    const { balanceAvailability, isUnavailable } =
      getValidateBalanceResultForEdit({
        availableSuper: !!availableInSuper,
        availableIDPS: !!availableInIDPS,
        hasSuper: hasSuperBalance,
        hasIDPS: hasIDPSBalance,
        hasWeight: Number(weight) > 0,
      });

    return {
      ...selectedInvestment,
      value,
      isFavourited: existingInvestment?.isFavourited ?? false,
      minimumInvestmentSize: existingInvestment?.minimumInvestmentSize,
      availableInSuper: existingInvestment?.availableInSuper ?? true,
      availableInIDPS: existingInvestment?.availableInIDPS ?? true,
      isMissingInvestment: !existingInvestment,
      balanceAvailability,
      isUnavailable,
    };
  });
};

export const mapFeeCalculatorDataForTemplateReplacement = ({
  productName,
  investmentTypeSelection,
  originalFeeCalculators,
  balanceAvailabilities,
  templateFeeCalculatorData,
  total,
}: {
  productName: string;
  investmentTypeSelection: PortfolioInvestmentTypeSelection;
  balanceAvailabilities: BalanceAvailabilitiesParams;
  originalFeeCalculators: FeeCalculatorDTO[];
  templateFeeCalculatorData: PortfolioTemplateFeeCalculatorDataDTO[];
  total: number;
}) => {
  const updatedFeeCalculators: FeeCalculatorDTO[] = originalFeeCalculators?.map(
    (item) => {
      const existingItemInTemplate = templateFeeCalculatorData?.find(
        (i) => i.variableName === item.variableName,
      );

      const newWeight = existingItemInTemplate?.weight || 0;
      const newValue = String(
        calculateInvestmentValueByWeight({
          total: total,
          weight: newWeight,
        }),
      );

      return {
        ...item,
        feeCalculatorData: {
          ...item.feeCalculatorData,
          weight: newWeight,
          value: newValue,
        },
      };
    },
  );

  const feeCalculatorList = getEditFeeCalculatorsByVariables({
    productName: productName,
    subProductName: "",
    updatedFeeCalculators,
    investmentTypeSelection,
    balanceAvailabilities,
  });

  return mapFeeCalculatorsToFeeCalculatorsData(feeCalculatorList, total);
};

export const mapFeeCalculatorsToFeeCalculatorsData = (
  feeCalculators: FeeCalculatorDTO[],
  portfolioTotal?: number,
): PortfolioFeeCalculatorDataDTO[] => {
  return feeCalculators.map((feeCalculator) => {
    const { isMissingInvestment, variableName, feeCalculatorData } =
      feeCalculator;
    const weight = feeCalculatorData?.weight ?? 0;

    const value = calculateInvestmentValueByWeight({
      total: portfolioTotal ?? 0,
      weight,
    });

    return {
      isMissingInvestment,
      variableName,
      value: value.toString(),
      weight,
      balanceAvailability: feeCalculator.balanceAvailability,
      hasUnavailableInvestmentOption:
        feeCalculator.hasUnavailableInvestmentOption,
    };
  });
};

export const mapFeeCalculatorsDataToFeeCalculator = (
  feeCalculatorsData: PortfolioFeeCalculatorDataDTO[],
  feeCalculators: FeeCalculatorDTO[],
): FeeCalculatorDTO[] => {
  return feeCalculatorsData.reduce<FeeCalculatorDTO[]>(
    (
      acc: FeeCalculatorDTO[],
      feeCalculatorData: PortfolioFeeCalculatorDataDTO,
    ) => {
      const feeCalculator = feeCalculators.find(
        (fc) => fc.variableName === feeCalculatorData.variableName,
      );

      if (feeCalculator) {
        const calculator: FeeCalculatorDTO = {
          ...feeCalculator,
          isMissingInvestment: feeCalculatorData.isMissingInvestment,
          feeCalculatorData: {
            ...feeCalculator.feeCalculatorData,
            weight: feeCalculatorData.weight,
            value: feeCalculatorData.value,
          },
          balanceAvailability: feeCalculatorData.balanceAvailability,
          hasUnavailableInvestmentOption:
            feeCalculatorData.hasUnavailableInvestmentOption,
        };

        acc.push(calculator);
      }

      return acc as FeeCalculatorDTO[];
    },
    [] as FeeCalculatorDTO[],
  );
};

export const getShowInPortfolioFeeCalculators = (
  feeCalculators: FeeCalculatorDTO[],
): PortfolioFeeCalculatorDataDTO[] =>
  mapFeeCalculatorsToFeeCalculatorsData(
    feeCalculators?.filter((item) => item.showInPortfolio),
  ) ?? [];

export const getCreatePortfolioFeeCalculatorData = ({
  data,
  feeCalculators,
  investments,
  total,
}: {
  data: AnalysisFeePortfolioDTO;
  feeCalculators: FeeCalculatorDTO[];
  investments: InvestmentSelectionDTO[];
  total: number;
}): AnalysisFeePortfolioDTO => {
  const belowTooltip: MAAnalysisTooltipDTO = {
    header: ErrorMessages.BelowMinimumInvestmentSize,
    additionalText: null,
    data: [],
  };

  data.selectedInvestments?.forEach((investment) => {
    const { displayName, weight, minimumInvestmentSize } = investment;
    const value = calculateInvestmentValueByWeight({
      total,
      weight: weight ?? 0,
    });
    const isBelowMinimumInvestment =
      Number(value) < Number(minimumInvestmentSize ?? 0);

    if (isBelowMinimumInvestment) {
      const dataTooltip: MAAnalysisTooltipDataDTO = {
        fieldType: "Currency",
        name: displayName ?? "",
        value: (value - Number(minimumInvestmentSize ?? 0)).toFixed(2),
      };

      belowTooltip.data?.push(dataTooltip);
    }
  });

  const newData: AnalysisFeePortfolioDTO = {
    ...data,
    name: "",
    belowMinimumInvestmentSizeTooltip: !isEmpty(belowTooltip?.data)
      ? belowTooltip
      : null,
    feeCalculatorData: getShowInPortfolioFeeCalculators(feeCalculators)?.map(
      (item) => {
        const existingCalculatorData = data.feeCalculatorData?.find(
          (x) => x.variableName === item.variableName,
        );

        const shouldSaveZeroWeight =
          !existingCalculatorData ||
          Number(existingCalculatorData.weight) <
            Configs.MinimumInvestmentWeightToSave;
        const newItem: PortfolioFeeCalculatorDataDTO = {
          ...item,
          weight: !shouldSaveZeroWeight ? existingCalculatorData?.weight : 0,
          value: !shouldSaveZeroWeight ? existingCalculatorData?.value : "0",
        };
        return newItem;
      },
    ),
    selectedInvestments: data.selectedInvestments?.map((item) => {
      const shouldSaveZeroWeight =
        Number(item.weight) < Configs.MinimumInvestmentWeightToSave;
      return {
        ...item,
        weight: !shouldSaveZeroWeight ? item?.weight : 0,
        value: !shouldSaveZeroWeight ? item?.value : 0,
      };
    }),
    favouritedInvestmentShCode: investments
      .filter((item) => item.isFavourited)
      .map((item) => item.shCode),
    unFavouritedInvestmentShCode: investments
      .filter((item) => !item.isFavourited)
      .map((item) => item.shCode),
  };

  return getEssentialPlusValues(newData);
};

export const getVariableNamesBasedOnInvestmentOption = (
  investmentOption: string,
): string[] => {
  switch (investmentOption as AnalysisVariable) {
    case AnalysisVariable.Choose_MF:
      return [
        AnalysisVariable.MF_balance_i,
        AnalysisVariable.Edge_eligible_MF_balance,
        AnalysisVariable.NorthSelect_MF_balance,
      ];
    case AnalysisVariable.Choose_AU:
      return [AnalysisVariable.AU_balance_i];
    case AnalysisVariable.Choose_Intl:
      return [AnalysisVariable.Intl_balance_i];
    case AnalysisVariable.Choose_TD:
      return [AnalysisVariable.TD_balance_i];
    case AnalysisVariable.Choose_ret_ins:
      return [];
    case AnalysisVariable.Choose_bond:
      return [AnalysisVariable.Bond_balance_i];
    default:
      return [];
  }
};

export const getSelectedPortfolioIds = (
  arr: AnalysisFeePortfolioSetupDTO[],
): string[] => {
  return arr.reduce((acc: string[], cur) => {
    if (
      cur != null &&
      !isNil(cur.selectedPortfolioId) &&
      !acc.includes(cur.selectedPortfolioId)
    ) {
      acc.push(cur.selectedPortfolioId);
    }
    return acc;
  }, []);
};

export const portfolioInvestmentTypeCheckboxValidation = (
  investmentOption: string,
  portfolioLevel: FeeCalculatorPortfolioLevel,
  portfolios?: AnalysisFeePortfolioDTO[],
  portfolioSetups?: AnalysisFeePortfolioSetupDTO[],
  excludeUnAssignPortfolio?: boolean,
) => {
  let isValid = true;

  if (portfolioLevel !== FeeCalculatorPortfolioLevel.Family) {
    return isValid;
  }

  if (portfolios) {
    const variableNames =
      getVariableNamesBasedOnInvestmentOption(investmentOption);

    const selectedPortfolioIds = getSelectedPortfolioIds(portfolioSetups ?? []);

    const portfolioToCheck = excludeUnAssignPortfolio
      ? portfolios.filter((item) => selectedPortfolioIds.includes(item.id))
      : portfolios;

    isValid = !portfolioToCheck.some((portfolio) => {
      const feeCalculatorData = portfolio.feeCalculatorData ?? [];
      return feeCalculatorData
        .filter((data) => variableNames.includes(data.variableName))
        .some((data) => (data.weight ?? 0) > 0);
    });
  }

  return isValid;
};

export const getPortfolioAssignmentItem = (
  portfolioSetup: AnalysisFeePortfolioSetupDTO,
): PortfolioAssignmentItemDTO => {
  return {
    subProductId:
      portfolioSetup.setupType === AnalysisFeePortfolioSetupType.RateCard
        ? portfolioSetup.subProductId ?? ""
        : portfolioSetup.id,
    rateCardId:
      portfolioSetup.setupType === AnalysisFeePortfolioSetupType.RateCard
        ? portfolioSetup.id
        : null,
    rateCardVersion:
      portfolioSetup.setupType === AnalysisFeePortfolioSetupType.RateCard
        ? portfolioSetup.rateCardVersion
        : null,
    portfolioId: portfolioSetup.selectedPortfolioId ?? "",
  };
};

export const checkPortfolioAllocationError = ({
  selectedPortfolioId,
  portfolioSetup,
  portfolios,
  feeCalculators,
  investmentTypeSelection,
  hasIDPSBalance,
  hasSuperBalance,
}: {
  selectedPortfolioId: string;
  portfolioSetup: AnalysisFeePortfolioSetupDTO;
  portfolios: AnalysisFeePortfolioDTO[];
  feeCalculators: FeeCalculatorDTO[];
  investmentTypeSelection: PortfolioInvestmentTypeSelection;
  hasIDPSBalance: boolean;
  hasSuperBalance: boolean;
}): boolean => {
  const portfolio = portfolios?.find((x) => x.id === selectedPortfolioId);

  const feeCalculatorToCheck = getEditFeeCalculatorsByVariables({
    productName: portfolioSetup?.product?.name || "",
    subProductName: portfolioSetup?.name || "",
    updatedFeeCalculators: mapFeeCalculatorsDataToFeeCalculator(
      portfolio?.feeCalculatorData ?? [],
      feeCalculators ?? [],
    ),
    investmentTypeSelection: investmentTypeSelection,
    balanceAvailabilities: { hasIDPSBalance, hasSuperBalance },
  });

  const feeCalculatorVariable = feeCalculatorToCheck.map(
    (item) => item.variableName as string,
  );

  const feeCalculatorTotal =
    portfolio?.feeCalculatorData
      .filter((item) => feeCalculatorVariable.includes(item.variableName))
      .reduce((sum, item) => sum + (item.weight ?? 0), 0) ?? 0;

  const sumInvestmentWeight =
    portfolio?.selectedInvestments.reduce(
      (sum, item) => sum + (item.weight ?? 0),
      0,
    ) ?? 0;

  return (
    parseFloat(
      (feeCalculatorTotal + sumInvestmentWeight).toFixed(
        Configs.DefaultDecimalPlaces,
      ),
    ) !== 100
  );
};

export const compileOverwritePortfolioValidationResult = ({
  portfolioSetups,
  portfolioAssignmentItems,
  portfolioValidation,
  portfolios,
  feeCalculators,
  investmentTypeSelection,
  hasIDPSBalance,
  hasSuperBalance,
}: {
  portfolioSetups: AnalysisFeePortfolioSetupDTO[];
  portfolioAssignmentItems?: PortfolioAssignmentItemDTO[];
  portfolioValidation?: PortfolioAssignmentValidationResultDTO[];
  portfolios: AnalysisFeePortfolioDTO[];
  feeCalculators: FeeCalculatorDTO[];
  investmentTypeSelection: PortfolioInvestmentTypeSelection;
  hasIDPSBalance: boolean;
  hasSuperBalance: boolean;
}): AnalysisFeePortfolioSetupDTO[] => {
  let portfolioSetupsCloned = cloneDeep(portfolioSetups) || [];

  portfolioAssignmentItems?.forEach((portfolioAssignmentItem) => {
    const {
      rateCardId: assignmentRateCardId,
      subProductId: assignmentSubProductId,
      rateCardVersion: assignmentRateCardVersion,
    } = portfolioAssignmentItem;

    portfolioSetupsCloned?.forEach((portfolioSetup) => {
      const { id, setupType, selectedPortfolioId } = portfolioSetup;
      const isRateCard = setupType === AnalysisFeePortfolioSetupType.RateCard;
      const rateCardVersion = portfolioSetup.rateCardVersion ?? null;

      const assignmentItemId = isRateCard
        ? assignmentRateCardId
        : assignmentSubProductId;
      const matched =
        id === assignmentItemId &&
        rateCardVersion === assignmentRateCardVersion;

      if (!matched) return;

      const validateResult = portfolioValidation?.find(
        (i) => i.id === id && i.rateCardVersion === rateCardVersion,
      );
      const isValid = !validateResult;

      let { hasAllocationError } = validateResult || {};
      if (!hasAllocationError && !!selectedPortfolioId) {
        hasAllocationError = checkPortfolioAllocationError({
          selectedPortfolioId: selectedPortfolioId ?? "",
          portfolioSetup,
          portfolios,
          feeCalculators,
          investmentTypeSelection,
          hasIDPSBalance,
          hasSuperBalance,
        });
      }

      if (isValid) {
        portfolioSetup.isMissingInvestmentOption = false;
        portfolioSetup.missingInvestmentOptionTooltip = null;
        portfolioSetup.isMissingInvestment = false;
        portfolioSetup.missingInvestmentTooltip = null;
        portfolioSetup.isBelowMinimumInvestmentSize = false;
        portfolioSetup.belowMinimumInvestmentSizeTooltip = null;
        portfolioSetup.hasAllocationError = hasAllocationError;
        portfolioSetup.hasUnavailableInvestmentOption = false;
        portfolioSetup.unavailableInvestmentOptionTooltip = null;
        portfolioSetup.hasUnavailableInvestment = false;
        portfolioSetup.unavailableInvestmentTooltip = null;

        return;
      }

      const {
        isMissingInvestment,
        isMissingInvestmentOption,
        missingInvestmentTooltip,
        missingInvestmentOptionTooltip,
        isBelowMinimumInvestmentSize,
        belowMinimumInvestmentSizeTooltip,
        hasUnavailableInvestmentOption,
        unavailableInvestmentOptionTooltip,
        hasUnavailableInvestment,
        unavailableInvestmentTooltip,
      } = validateResult || {};

      portfolioSetup.hasAllocationError = hasAllocationError;
      portfolioSetup.isMissingInvestment = isMissingInvestment;
      portfolioSetup.missingInvestmentTooltip = missingInvestmentTooltip;
      portfolioSetup.isMissingInvestmentOption = isMissingInvestmentOption;
      portfolioSetup.missingInvestmentOptionTooltip =
        missingInvestmentOptionTooltip;
      portfolioSetup.isBelowMinimumInvestmentSize =
        isBelowMinimumInvestmentSize;
      portfolioSetup.belowMinimumInvestmentSizeTooltip =
        belowMinimumInvestmentSizeTooltip;
      portfolioSetup.hasUnavailableInvestmentOption =
        hasUnavailableInvestmentOption;
      portfolioSetup.unavailableInvestmentOptionTooltip =
        unavailableInvestmentOptionTooltip;
      portfolioSetup.hasUnavailableInvestment = hasUnavailableInvestment;
      portfolioSetup.unavailableInvestmentTooltip =
        unavailableInvestmentTooltip;
    });
  });

  // TODO: Hardcode for Praemium Spectrum, need refactor
  portfolioSetupsCloned = validatePraemiumSpectrumSuperCashBalance({
    portfolioSetups: portfolioSetupsCloned,
    portfolios,
    hasIDPSBalance,
    hasSuperBalance,
  });

  return portfolioSetupsCloned;
};

/**
 * @hardcoded
 * Hardcode for Praemium Spectrum - Super product to validate Super Cash Balance
 *
 * When implementing other products with similar validation,
 * consider refactoring this function to be more generic.
 */
const validatePraemiumSpectrumSuperCashBalance = ({
  portfolioSetups,
  portfolios,
  hasIDPSBalance,
  hasSuperBalance,
}: {
  portfolioSetups: AnalysisFeePortfolioSetupDTO[];
  portfolios: AnalysisFeePortfolioDTO[];
  hasIDPSBalance: boolean;
  hasSuperBalance: boolean;
}) =>
  portfolioSetups?.map((portfolioSetup) => {
    const { name, product, setupType, selectedPortfolioId } = portfolioSetup;
    const isRateCard = setupType === AnalysisFeePortfolioSetupType.RateCard;
    if (isRateCard || !selectedPortfolioId) return portfolioSetup;
    const isPraemiumSpectrum =
      product?.name === GlobalProductNames.Praemium &&
      name === GlobalSubProductNames.PraemiumSpectrum;
    if (!isPraemiumSpectrum) return portfolioSetup;

    portfolioSetup.cashAvailableInSuper = false;

    const hasCashInSuper = !!portfolios?.find((portfolio) => {
      if (portfolio.id !== selectedPortfolioId) return false;
      return portfolio.feeCalculatorData?.some(
        (fc) =>
          fc.variableName === AnalysisVariable.Super_Cash_balance &&
          Number(fc.weight) > 0 &&
          !hasIDPSBalance &&
          !!hasSuperBalance,
      );
    });

    portfolioSetup.hasCashNotAvailableInSuperError = hasCashInSuper;

    return portfolioSetup;
  });

export const getSetupItemWarningData = (
  setup: AnalysisFeePortfolioSetupDTO,
) => {
  const {
    belowMinimumInvestmentSizeTooltip,
    isBelowMinimumInvestmentSize,
    selectedPortfolioId,
  } = setup || {};
  if (!isBelowMinimumInvestmentSize || !selectedPortfolioId) return null;

  return [
    {
      title: ErrorMessages.BelowMinimumInvestmentSize,
      tooltip: belowMinimumInvestmentSizeTooltip,
    },
  ];
};

export const getSetupItemErrorData = (setup: AnalysisFeePortfolioSetupDTO) => {
  const {
    isMissingInvestment,
    missingInvestmentTooltip,
    isMissingInvestmentOption,
    missingInvestmentOptionTooltip,
    hasAllocationError,
    selectedPortfolioId,
    hasUnavailableInvestmentOption,
    unavailableInvestmentOptionTooltip,
    hasUnavailableInvestment,
    unavailableInvestmentTooltip,
    hasCashNotAvailableInSuperError,
  } = setup || {};

  if (!selectedPortfolioId) return null;

  const errorData = [];

  if (hasAllocationError) {
    errorData.push({
      title: ErrorMessages.AllocationMustSumTo100,
      tooltip: null,
    });
  }

  if (hasCashNotAvailableInSuperError) {
    errorData.push({
      title: ErrorMessages.CashNotAvailableInSuperError,
      tooltip: null,
    });
  }

  if (isMissingInvestment) {
    errorData.push({
      title: ErrorMessages.MissingInvestment,
      tooltip: missingInvestmentTooltip,
    });
  }

  if (isMissingInvestmentOption) {
    errorData.push({
      title: ErrorMessages.MissingInvestmentOption,
      tooltip: missingInvestmentOptionTooltip,
    });
  }

  if (hasUnavailableInvestmentOption) {
    errorData.push({
      title: ErrorMessages.InvestmentOptionNotAvailable,
      tooltip: unavailableInvestmentOptionTooltip,
    });
  }

  if (hasUnavailableInvestment) {
    errorData.push({
      title: ErrorMessages.InvestmentNotAvailable,
      tooltip: unavailableInvestmentTooltip,
    });
  }

  return isEmpty(errorData) ? null : errorData;
};

export const getSetupRowWarningMessage = ({
  balanceAvailability,
}: {
  balanceAvailability: BalanceAvailability;
}) => {
  const displayData = [];
  const shouldWarningSuper =
    balanceAvailability === BalanceAvailability.WarningSuper;
  const shouldWarningIDPS =
    balanceAvailability === BalanceAvailability.WarningIDPS;
  const shouldWarningBoth = balanceAvailability === BalanceAvailability.Warning;

  shouldWarningSuper &&
    displayData.push({
      title: ErrorMessages.NotAvailableInSuper,
    });
  shouldWarningIDPS &&
    displayData.push({
      title: ErrorMessages.NotAvailableInIDPS,
    });
  shouldWarningBoth &&
    displayData.push({
      title: ErrorMessages.NotAvailableInIDPSAndSuper,
    });

  return isEmpty(displayData) ? null : displayData;
};

export const getSetupRowErrorMessage = ({
  isCreateMode,
  balanceAvailability,
  isUnavailable,
  isMissingInvestments,
  isMissingInvestmentOption,
  isIDPSBalance,
  isSuperBalance,
}: {
  isCreateMode?: boolean;
  balanceAvailability: BalanceAvailability;
  isUnavailable?: boolean;
  isMissingInvestments?: boolean;
  isMissingInvestmentOption?: boolean;
  isIDPSBalance?: boolean;
  isSuperBalance?: boolean;
}) => {
  const errorData = [];

  if (isMissingInvestments) {
    errorData.push({ title: ErrorMessages.MissingInvestment });
    return errorData;
  }

  if (isMissingInvestmentOption) {
    switch (true) {
      case isIDPSBalance:
        errorData.push({
          title: ErrorMessages.NoIDPSBalanceIncluded,
        });
        break;
      case isSuperBalance:
        errorData.push({
          title: ErrorMessages.NoSuperBalanceIncluded,
        });
        break;
      default:
        errorData.push({
          title: ErrorMessages.MissingInvestmentOption,
        });
        break;
    }
  }

  const shouldDisplayErrorSuper =
    balanceAvailability === BalanceAvailability.MissingSuper && !isCreateMode;
  shouldDisplayErrorSuper &&
    errorData.push({ title: ErrorMessages.NotAvailableInSuper });

  const shouldDisplayErrorIDPS =
    balanceAvailability === BalanceAvailability.MissingIDPS && !isCreateMode;
  shouldDisplayErrorIDPS &&
    errorData.push({ title: ErrorMessages.NotAvailableInIDPS });

  const shouldDisplayErrorBoth =
    balanceAvailability === BalanceAvailability.Missing &&
    !isCreateMode &&
    isUnavailable;
  shouldDisplayErrorBoth &&
    errorData.push({
      title: ErrorMessages.NotAvailableInIDPSAndSuper,
    });

  return isEmpty(errorData) ? null : errorData;
};

//#region Private methods
const getBaseVariables = (
  hasIDPSBalance: boolean,
  hasSuperBalance: boolean,
  isEditMode = false,
): string[] => {
  if (isEditMode) return FeeAnalysisBalanceVariableNames.CashBalances;

  switch (true) {
    case !hasIDPSBalance && !hasSuperBalance:
      return [];
    case hasIDPSBalance && !hasSuperBalance:
      return [FeeAnalysisBalanceVariableNames.IDPS_Cash_balance];
    case !hasIDPSBalance && hasSuperBalance:
      return [FeeAnalysisBalanceVariableNames.Super_Cash_balance];
    default:
      return FeeAnalysisBalanceVariableNames.CashBalances;
  }
};

const getTermDepositVariables = (isChooseTD: boolean): string[] => {
  return isChooseTD ? FeeAnalysisBalanceVariableNames.TermDepositsBalances : [];
};

const getManagedFundVariables = (isChooseMF: boolean): string[] => {
  return isChooseMF ? FeeAnalysisBalanceVariableNames.ManagedFundsBalances : [];
};

const getAustralianVariables = (isChooseAU: boolean): string[] => {
  return isChooseAU
    ? FeeAnalysisBalanceVariableNames.AustralianSharesBalances
    : [];
};

const getInternationalVariables = (isChooseIntl: boolean): string[] => {
  return isChooseIntl
    ? FeeAnalysisBalanceVariableNames.InternationalBalances
    : [];
};

const getUnlistBondVariables = (isChooseBond: boolean): string[] => {
  return isChooseBond ? FeeAnalysisBalanceVariableNames.BondBalances : [];
};

const filterSpecialVariableNames = (
  productName: string,
  selectedVariables: string[],
  type: "create" | "edit" = "create",
): string[] => {
  const additionalVariableMap =
    FeeAnalysisSpecialProductNames.AdditionalVariableProductMap;
  const specialVariableNames = additionalVariableMap.get(productName);
  const flatVariableNames = Array.from(additionalVariableMap.values()).flat();

  if (specialVariableNames) {
    return type === "create"
      ? difference(selectedVariables, specialVariableNames)
      : selectedVariables;
  }

  return type === "create"
    ? difference(selectedVariables, flatVariableNames)
    : selectedVariables;
};

export const getValidateBalanceResultForCreate = ({
  availableIDPS,
  availableSuper,
  hasIDPS,
  hasSuper,
}: {
  availableIDPS: boolean;
  availableSuper: boolean;
  hasIDPS: boolean;
  hasSuper: boolean;
}): { balanceAvailability: BalanceAvailability; isUnavailable: boolean } => {
  const result = {
    balanceAvailability: BalanceAvailability.Show,
    isUnavailable: false,
  };

  // Both available - always show
  if (availableIDPS && availableSuper) {
    return result;
  }

  // Neither available - always hidden
  if (!availableIDPS && !availableSuper) {
    result.balanceAvailability = BalanceAvailability.Warning;
    result.isUnavailable = true;
    return result;
  }

  // Early return for no balances
  if (!hasIDPS && !hasSuper) {
    result.balanceAvailability = BalanceAvailability.Hidden;
    result.isUnavailable = true;
    return result;
  }

  // Only IDPS available
  if (availableIDPS && !availableSuper) {
    if (hasIDPS && !hasSuper) return result;
    if (!hasIDPS && hasSuper) {
      result.balanceAvailability = BalanceAvailability.WarningSuper;
      result.isUnavailable = true;
      return result;
    }
    result.balanceAvailability = BalanceAvailability.WarningSuper; // hasIDPS && hasSuper
    return result;
  }

  // Only Super available
  if (!availableIDPS && availableSuper) {
    if (!hasIDPS && hasSuper) return result;
    if (hasIDPS && !hasSuper) {
      result.balanceAvailability = BalanceAvailability.WarningIDPS;
      result.isUnavailable = true;
      return result;
    }
    result.balanceAvailability = BalanceAvailability.WarningIDPS; // hasIDPS && hasSuper
    return result;
  }

  return result;
};

export const getValidateBalanceResultForEdit = ({
  availableIDPS,
  availableSuper,
  hasIDPS,
  hasSuper,
  hasWeight,
}: {
  availableIDPS: boolean;
  availableSuper: boolean;
  hasIDPS: boolean;
  hasSuper: boolean;
  hasWeight: boolean;
}): { balanceAvailability: BalanceAvailability; isUnavailable: boolean } => {
  // Early return for no balances - always hidden
  if (!hasIDPS && !hasSuper) {
    return {
      balanceAvailability: BalanceAvailability.Hidden,
      isUnavailable: true,
    };
  }

  // Both available - always show
  if (availableIDPS && availableSuper) {
    return {
      balanceAvailability: BalanceAvailability.Show,
      isUnavailable: false,
    };
  }

  // Neither available
  if (!availableIDPS && !availableSuper) {
    if (hasIDPS && hasSuper)
      return {
        balanceAvailability: hasWeight
          ? BalanceAvailability.Missing
          : BalanceAvailability.Warning,
        isUnavailable: true,
      };

    if (hasIDPS)
      return {
        balanceAvailability: hasWeight
          ? BalanceAvailability.MissingIDPS
          : BalanceAvailability.WarningIDPS,
        isUnavailable: true,
      };

    return {
      balanceAvailability: hasWeight
        ? BalanceAvailability.MissingSuper
        : BalanceAvailability.WarningSuper,
      isUnavailable: true,
    };
  }

  // Only IDPS available
  if (availableIDPS) {
    if (hasIDPS && !hasSuper)
      return {
        balanceAvailability: BalanceAvailability.Show,
        isUnavailable: false,
      };

    if (hasIDPS && hasSuper)
      return {
        balanceAvailability: BalanceAvailability.WarningSuper,
        isUnavailable: false,
      };

    return {
      balanceAvailability: hasWeight
        ? BalanceAvailability.MissingSuper
        : BalanceAvailability.WarningSuper,
      isUnavailable: true,
    }; // !hasIDPS && hasSuper
  }

  // Only Super available
  if (!hasIDPS && hasSuper)
    return {
      balanceAvailability: BalanceAvailability.Show,
      isUnavailable: false,
    };

  if (hasIDPS && hasSuper)
    return {
      balanceAvailability: BalanceAvailability.WarningIDPS,
      isUnavailable: false,
    };

  return {
    balanceAvailability: hasWeight
      ? BalanceAvailability.MissingIDPS
      : BalanceAvailability.WarningIDPS,
    isUnavailable: true,
  }; // hasIDPS && !hasSuper
};

const getBalanceAvailabilitiesByVariableName = (
  variableName: AnalysisVariable,
  balanceAvailabilities: BalanceAvailabilitiesParams,
): {
  hasIDPS: boolean;
  hasSuper: boolean;
  availableIDPS: boolean;
  availableSuper: boolean;
} => {
  const { hasIDPSBalance, hasSuperBalance } = balanceAvailabilities || {};

  const result = {
    hasIDPS: hasIDPSBalance,
    hasSuper: hasSuperBalance,
    availableIDPS: true,
    availableSuper: true,
  };

  const mapping = VariableNameToBalanceMap[variableName];

  if (!mapping) return result;

  const { availableIDPSKey, availableSuperKey } = mapping;
  result.availableIDPS = !!balanceAvailabilities[availableIDPSKey];
  result.availableSuper = !!balanceAvailabilities[availableSuperKey];

  return result;
};

const validateCreatePortfolioBalances = ({
  variableName,
  balanceAvailabilities,
}: {
  variableName: AnalysisVariable;
  balanceAvailabilities: BalanceAvailabilitiesParams;
}) => {
  const variableBalanceAvailabilities = getBalanceAvailabilitiesByVariableName(
    variableName,
    balanceAvailabilities,
  );

  return getValidateBalanceResultForCreate(variableBalanceAvailabilities);
};

const validateEditPortfolioBalances = ({
  variableName,
  balanceAvailabilities,
  hasWeight,
}: {
  variableName: AnalysisVariable;
  balanceAvailabilities: BalanceAvailabilitiesParams;
  hasWeight: boolean;
}) => {
  const variableBalanceAvailabilities = getBalanceAvailabilitiesByVariableName(
    variableName,
    balanceAvailabilities,
  );

  return getValidateBalanceResultForEdit({
    ...variableBalanceAvailabilities,
    hasWeight,
  });
};

const getEssentialPlusValues = (
  data: AnalysisFeePortfolioDTO,
): AnalysisFeePortfolioDTO => {
  const termDepositCalculator = data.feeCalculatorData?.find(
    (item) => item.variableName === AnalysisVariable.TD_balance_i,
  );
  const auListedInvestmentsCalculator = data.feeCalculatorData?.find(
    (item) => item.variableName === AnalysisVariable.AU_balance_i,
  );

  if (!termDepositCalculator || !auListedInvestmentsCalculator) return data;

  return {
    ...data,
    feeCalculatorData: data.feeCalculatorData?.map((item) => {
      if (item.variableName === AnalysisVariable.EssentialPlus_TD_balance) {
        return {
          ...item,
          value: termDepositCalculator.value,
          weight: termDepositCalculator.weight,
        };
      }

      if (item.variableName === AnalysisVariable.EssentialPlus_ETF_balance) {
        return {
          ...item,
          value: auListedInvestmentsCalculator.value,
          weight: auListedInvestmentsCalculator.weight,
        };
      }

      return item;
    }),
  };
};

const createDefaultCalculatorDTO = (
  calculator: FeeCalculatorDTO,
): FeeCalculatorDTO => ({
  ...calculator,
  feeCalculatorData: {
    ...calculator.feeCalculatorData,
    weight: 0,
    value: "0",
  },
});

const createEditCalculatorDTO = (
  calculator: FeeCalculatorDTO,
): FeeCalculatorDTO => ({
  ...calculator,
  feeCalculatorData: {
    ...calculator.feeCalculatorData,
    value: "0",
  },
});
//#endregion
